import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { connect } from "formik";
import { get } from "lodash";
import { floatingIndexList } from "constants/floatingIndexes";
import { compose } from "redux";
import { Grid } from "@material-ui/core";
import { FormikInputPercent, FormikInputNumber, DEAL_STATUSES, LOAN_TYPES, FormikInputMoney } from "@loanbossinc/component-library";
import { INTEREST_RATE_TYPE_IDS } from "modals/RefiCashOutCalculatorModal/loanProceedsConstants";

import LenderInformation from "./lenderInformation";
import { shouldShowInterestRate, shouldShowLoanSpread, shouldShowLoanSenority } from "./quoteDetail.util";
import { Divider, StyledGridContainer, StyledFormikInputText, StyledFormikInputTypeAhead } from "./quoteDetails.styles";

const StyledFormikInputNumber = styled(FormikInputNumber)`&&&&{
  width: 100%
}`;
const StyledFormikInputPercent = styled(FormikInputPercent)`&&&&{
  width: 100%
}`;
const StyledFormikInputMoney = styled(FormikInputMoney)`&&&&{
  width: 100%
}`;

const getFixedFloating = values => {
  const rateTypeId = get(values, "details.rateTypeId");
  if (rateTypeId && rateTypeId === INTEREST_RATE_TYPE_IDS.FIXED_RATE) {
    return 1;
  } else if (rateTypeId && rateTypeId === INTEREST_RATE_TYPE_IDS.FLOATING_RATE) {
    return 2;
  } else {
    return 0;
  }
};

// eslint-disable-next-line max-lines-per-function
const DetailsTabContent = ({ formik, handleOpenRequestContactModal, isTermSheetOpen, deal }) => {
  const [defaultIndex, setDefaultIndex] = useState(0);
  const { values } = formik;
  const fixedIndexes = get(values, "ancillary.fixedIndexes", []);
  const floatingIndexes = floatingIndexList
    .filter(({ name }) => name !== "None")
    .map(({ value, name, label }) => ({
      id: value,
      name,
      label,
      value
    }));

  useEffect(() => {
    setDefaultIndex(getFixedFloating(values));
  }, [values, values.details?.rateTypeId]);


  let loanTypes = values && get(values, "ancillary.loanTypes", []);
  const isAddingSupplemental = deal && get(deal, "dealStatus.id") === DEAL_STATUSES.ADDING_SUPPLEMENTAL;
  if (isAddingSupplemental) {
    loanTypes = loanTypes.filter(loanType => loanType.id === LOAN_TYPES.FANNIE_MAE || loanType.id === LOAN_TYPES.FREDDIE_MAC);
  }
  const loanSenorityTypes = values && get(values, "ancillary.loanSenorityTypes", []);

  return (
    <Grid container>
      <LenderInformation
        formik={formik}
        onOpenRequestContactModal={handleOpenRequestContactModal}
      />
      <Divider />

      {!isTermSheetOpen &&
        <>
          {" "}
          <StyledGridContainer item xs={12}>
            <StyledFormikInputTypeAhead
              id="loanTypeId"
              name="details.loanTypeId"
              label="Loan Type *"
              options={loanTypes}
              displayWith={option => option && option.name}
              setValueWith={option => option && option.id}
              className="input"
              variant={"filled"}
            />
          </StyledGridContainer>

          {shouldShowLoanSenority &&
            <StyledGridContainer item xs={12}>
              <StyledFormikInputTypeAhead
                id="loanSeniorityId"
                name="details.loanSeniorityId"
                label="Loan Seniority"
                options={loanSenorityTypes}
                displayWith={option => option && option.name}
                setValueWith={option => option && option.id}
                className="input"
                variant={"filled"}
              />
            </StyledGridContainer>}
          <StyledGridContainer item xs={12}>
            <StyledFormikInputMoney
              name={"details.amount"}
              label={"Amount ($)"}
              variant={"filled"}
            />
          </StyledGridContainer>
          <StyledGridContainer container item xs={12}>
            <Grid item xs={5}>
              <StyledFormikInputPercent
                name={"covenants.ltvStabilized"}
                label={"LTV (%)"}
                decimals={3}
                variant={"filled"}
              />
            </Grid>
            <Grid item xs={2} />
            <Grid item xs={5}>
              <StyledFormikInputPercent
                name={"covenants.ltc"}
                label={"LTC (%)"}
                decimals={3}
                variant={"filled"}
              />
            </Grid>
          </StyledGridContainer>

          <StyledGridContainer item xs={12}>
            <StyledFormikInputNumber
              name={"details.loanTermMonths"}
              label={"Term (months) *"}
              variant={"filled"}
            />
          </StyledGridContainer>

          <Divider />

          <StyledGridContainer item xs={12}>
            <StyledFormikInputTypeAhead
              id="rateTypeId"
              name="details.rateTypeId"
              label="Rate Type *"
              options={[
                { name: "Fixed", id: INTEREST_RATE_TYPE_IDS.FIXED_RATE },
                { name: "Floating", id: INTEREST_RATE_TYPE_IDS.FLOATING_RATE }
              ]}
              displayWith={option => option && option.name}
              setValueWith={option => option && option.id}
              className="input"
              variant={"filled"}
            />
          </StyledGridContainer>

          <Divider />

          <StyledGridContainer item xs={12}>
            {defaultIndex === 1 && <StyledFormikInputTypeAhead
              id="fixedIndex-indexId"
              name="details.indexId"
              label="Index*"
              options={fixedIndexes}
              displayWith={option => option && option.name}
              setValueWith={option => option && option.id}
              className="input"
              variant={"filled"}
            />}
            {defaultIndex === 2 && 
              <StyledFormikInputTypeAhead
                id="floatingIndex-indexId"
                name="details.indexId"
                label="Index*"
                options={floatingIndexes}
                displayWith={option => option && option.name}
                setValueWith={option => option && option.id}
                className="input"
                variant={"filled"}
              />}
          </StyledGridContainer>

          {shouldShowInterestRate(defaultIndex, values) &&
            <StyledGridContainer container item xs={12}>
              <StyledFormikInputPercent
                name={"details.interestRate"}
                label={"Rate (%) *"}
                decimals={3}
                variant={"filled"}
              />
            </StyledGridContainer>}

          {shouldShowLoanSpread(values) &&
            <StyledGridContainer container item xs={12}>
              <StyledFormikInputPercent
                name={"details.loanSpread"}
                label={"Spread (%) *"}
                decimals={3}
                variant={"filled"}
              />
            </StyledGridContainer>}

          <StyledGridContainer container item xs={12}>
            <StyledFormikInputText
              name={"closingCosts.rateLockFeeAmount"}
              label={"Rate Lock (%)"}
              variant={"filled"}
            />
          </StyledGridContainer>

          <StyledGridContainer container item xs={12}>
            <StyledFormikInputPercent
              name={"closingCosts.originationFeePercent"}
              label={"Origination Fee (%)"}
              decimals={3}
              variant={"filled"}
            />
          </StyledGridContainer>
        </>}
    </Grid>
  );
};

export default compose(connect)(DetailsTabContent);
